import { gql, useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import SearchBar from "components/base/SearchBar";
import Spinner from "components/base/Spinner";
import { useMemo, useState } from "react";
import { searchByProp } from "utils/search";
import Product from "./Product";

const FETCH_PRODUCTS_FOR_SALE = gql`
  query FETCH_PRODUCTS_FOR_SALE {
    allProducts(productType: "normal", isSalable: true, isActive: true) {
      id
      name
      number
      version
    }
  }
`;

const MultiProductSelector = ({ onDone, defaultSelectedProducts = [] }) => {
  const [searchText, setSearchText] = useState("");
  const [selectedProducts, setSelectedProducts] = useState(
    defaultSelectedProducts,
  );
  const { loading, error, data } = useQuery(FETCH_PRODUCTS_FOR_SALE);

  const selectedIdSet = useMemo(
    () => new Set(selectedProducts.map((product) => product.id)),
    [selectedProducts],
  );

  function toggleProduct(product) {
    if (selectedIdSet.has(product.id)) {
      setSelectedProducts((prev) =>
        prev.filter((item) => item.id !== product.id),
      );
      return;
    }
    setSelectedProducts((prev) => [...prev, product]);
  }

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  return (
    <div className="flex flex-col flex-1 max-h-[60vh]">
      <SearchBar
        bgColor="dark:bg-gray-900"
        autoFocus
        type="text"
        placeholder="Search by name or number"
        value={searchText}
        onChange={setSearchText}
      />

      <div className="flex-1 mt-4 overflow-auto">
        {(data?.allProducts || [])
          .filter((product) =>
            searchByProp(product, ["name", "number"], searchText),
          )
          .map((product) => (
            <div
              key={product.id}
              className="flex items-center gap-3 border-b dark:border-gray-700 hover:text-blue-600 dark:hover:text-blue-300 cursor-pointer py-2"
              onClick={() => toggleProduct(product)}
            >
              <input
                type="checkbox"
                checked={selectedIdSet.has(product.id)}
                onChange={() => toggleProduct(product)}
                onClick={(e) => e.stopPropagation()}
              />
              <Product
                className="flex-1"
                name={product.name}
                number={product.number}
                version={product.version}
              />
            </div>
          ))}
      </div>

      <div className="pt-4 mt-2 border-t dark:border-gray-700 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="text-xs opacity-70">
            {selectedProducts.length} product
            {selectedProducts.length === 1 ? "" : "s"} selected
          </div>
          {selectedProducts.length > 0 ? (
            <Button
              danger
              className="text-xs"
              onClick={() => setSelectedProducts([])}
            >
              Clear Selection
            </Button>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <Button primary onClick={() => onDone(selectedProducts)}>
            Done
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MultiProductSelector;
